"use client";
import * as React from "react";
import { trpcReactQueryApi } from "~/trpc/react";
import {
  TaskStatus,
  TaskCategory,
  TaskImpact,
  TaskEffort,
} from "@constructr-sdk/entities";
import { UseProjectWorkspaceParams, UseProjectWorkspaceReturn } from "./use-project-workspace.interface";
import { toast } from "sonner";

// UUID validator
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
const isValidUuid = (id: string | null): boolean => !!id && UUID_RE.test(id);

/**
 * Domain hook for Project Workspace.
 * Manages project selection, task listing, filtering, and operations.
 */
export function useProjectWorkspace(params: UseProjectWorkspaceParams): UseProjectWorkspaceReturn {
  const { autoLoad = false, selectedProjectId: initialSelectedProjectId = null } = params;

  // Local state
  const [projectSearchQuery, setProjectSearchQuery] = React.useState("");
  const [selectedProjectId, setSelectedProjectId] = React.useState<string | null>(initialSelectedProjectId);
  const [filters, setFilters] = React.useState({
    query: "",
    status: "all" as TaskStatus | "all",
    category: "all" as TaskCategory | "all",
    impact: "all" as TaskImpact | "all",
  });
  const [pagination, setPagination] = React.useState({ page: 1, pageSize: 10, total: 0 });
  const [selectedTaskIds, setSelectedTaskIds] = React.useState<string[]>([]);

  // TRPC utils for cache invalidation
  const utils = trpcReactQueryApi.useUtils();

  // Projects query
  const { data: projectsData, isLoading: projectsLoading } = trpcReactQueryApi.main.projects.all.useQuery(undefined, {
    enabled: autoLoad,
  });

  // Selected project query - uses selectedProjectId state
  const { data: projectData, isLoading: projectLoading } = trpcReactQueryApi.main.projects.byId.useQuery(
    { id: selectedProjectId! },
    { enabled: autoLoad && isValidUuid(selectedProjectId) }
  );

  // Tasks query - uses selectedProjectId state and current filters/pagination
  const { data: tasksData, isLoading: tasksLoading } = trpcReactQueryApi.main.tasks.list.useQuery(
    { 
      projectId: selectedProjectId!, 
      query: filters.query,
      status: filters.status,
      category: filters.category,
      impact: filters.impact,
      page: pagination.page, 
      pageSize: pagination.pageSize 
    },
    { enabled: autoLoad && isValidUuid(selectedProjectId) }
  );

  // KPIs query
  const { data: kpisData, isLoading: kpisLoading } = trpcReactQueryApi.main.kpis.getProjectKpis.useQuery(
    { projectId: selectedProjectId! },
    { enabled: autoLoad && isValidUuid(selectedProjectId) }
  );

  // Category breakdown query
  const { data: categoryBreakdownData, isLoading: categoryBreakdownLoading } = trpcReactQueryApi.main.kpis.getCategoryBreakdown.useQuery(
    { projectId: selectedProjectId! },
    { enabled: autoLoad && isValidUuid(selectedProjectId) }
  );

  // Recent activity query
  const { data: recentActivityData, isLoading: recentActivityLoading } = trpcReactQueryApi.main.kpis.getRecentActivity.useQuery(
    { projectId: selectedProjectId! },
    { enabled: autoLoad && isValidUuid(selectedProjectId) }
  );

  // Update pagination.total when tasksData changes
  React.useEffect(() => {
    if (typeof tasksData?.total === 'number') {
      setPagination(prev => ({ ...prev, total: tasksData.total }));
    }
  }, [tasksData?.total]);

  // Mutations
  const runAnalysisMutation = trpcReactQueryApi.main.analysis.runAnalysis.useMutation({
    onSuccess: async () => {
      // Keep invalidations but remove generic success toast
      await utils.main.projects.byId.invalidate();
      await utils.main.tasks.list.invalidate();
      await utils.main.kpis.getProjectKpis.invalidate();
      await utils.main.kpis.getCategoryBreakdown.invalidate();
      await utils.main.kpis.getRecentActivity.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Analysis failed");
    },
  });

  const createProjectMutation = trpcReactQueryApi.main.projects.create.useMutation({
    onSuccess: async () => {
      toast.success("Project created successfully");
      await utils.main.projects.all.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create project");
    },
  });

  const createTaskMutation = trpcReactQueryApi.main.tasks.create.useMutation({
    onSuccess: async () => {
      toast.success("Task created successfully");
      await utils.main.tasks.list.invalidate();
      await utils.main.kpis.getProjectKpis.invalidate();
      await utils.main.kpis.getCategoryBreakdown.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create task");
    },
  });

  const updateTaskMutation = trpcReactQueryApi.main.tasks.update.useMutation({
    onSuccess: async () => {
      toast.success("Task updated successfully");
      await utils.main.tasks.list.invalidate();
      await utils.main.kpis.getProjectKpis.invalidate();
      await utils.main.kpis.getCategoryBreakdown.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update task");
    },
  });

  const deleteTaskMutation = trpcReactQueryApi.main.tasks.delete.useMutation({
    onSuccess: async () => {
      toast.success("Task deleted successfully");
      await utils.main.tasks.list.invalidate();
      await utils.main.kpis.getProjectKpis.invalidate();
      await utils.main.kpis.getCategoryBreakdown.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete task");
    },
  });

  const toggleTaskCompletedMutation = trpcReactQueryApi.main.tasks.toggleCompleted.useMutation({
    onSuccess: async () => {
      toast.success("Task status updated");
      await utils.main.tasks.list.invalidate();
      await utils.main.kpis.getProjectKpis.invalidate();
      await utils.main.kpis.getCategoryBreakdown.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update task status");
    },
  });

  const exportCsvMutation = trpcReactQueryApi.main.tasks.exportCsv.useMutation({
    onError: (error) => {
      toast.error(error.message || "Failed to export CSV");
    },
  });

  const exportPdfMutation = trpcReactQueryApi.main.tasks.exportPdf.useMutation({
    onError: (error) => {
      toast.error(error.message || "Failed to export PDF");
    },
  });

  // Derived state
  const projects = projectsData ?? [];
  const selectedProject = projectData ?? null;
  const tasks = tasksData?.tasks ?? [];
  const kpis = kpisData ?? null;
  const categoryBreakdown = categoryBreakdownData ?? null;
  const recentActivity = recentActivityData ?? [];

  // Analysis failure state - would come from analysis run query in production
  const lastAnalysisFailed = false;
  const lastAnalysisErrorMessage = null;

  const isLoading = projectsLoading || projectLoading || tasksLoading || kpisLoading || categoryBreakdownLoading || recentActivityLoading;
  const error = null;

  // Derive isRunningAnalysis from mutation state
  const isRunningAnalysis = runAnalysisMutation.isPending;

  // Mutations (client-side state updates)
  const setTaskQuery = React.useCallback((query: string) => {
    setFilters((prev) => ({ ...prev, query }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const setTaskStatusFilter = React.useCallback((status: TaskStatus | "all") => {
    setFilters((prev) => ({ ...prev, status }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const setTaskCategoryFilter = React.useCallback((category: TaskCategory | "all") => {
    setFilters((prev) => ({ ...prev, category }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const setTaskImpactFilter = React.useCallback((impact: TaskImpact | "all") => {
    setFilters((prev) => ({ ...prev, impact }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const clearTaskFilters = React.useCallback(() => {
    setFilters({
      query: "",
      status: "all",
      category: "all",
      impact: "all",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const setPage = React.useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  const setPageSize = React.useCallback((pageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const setSelectedTaskIdsMutation = React.useCallback((ids: string[]) => {
    setSelectedTaskIds(ids);
  }, []);

  const toggleTaskSelected = React.useCallback((taskId: string) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  }, []);

  const clearSelection = React.useCallback(() => {
    setSelectedTaskIds([]);
  }, []);

  // Commands (server interactions)
  const initialize = React.useCallback(async (projectId: string | null) => {
    // Validate and set the selected project ID
    if (projectId && isValidUuid(projectId)) {
      setSelectedProjectId(projectId);
      // Queries will automatically refetch due to reactive dependencies
    } else {
      setSelectedProjectId(null);
    }
  }, []);

  const selectProject = React.useCallback(async (projectId: string) => {
    // Validate UUID before setting
    if (!isValidUuid(projectId)) {
      toast.error("Invalid project ID");
      return;
    }
    setSelectedProjectId(projectId);
    setPagination((prev) => ({ ...prev, page: 1 }));
    // Queries will automatically refetch due to reactive dependencies
  }, []);

  const runAnalysis = React.useCallback(async () => {
    if (!selectedProjectId || !isValidUuid(selectedProjectId)) {
      toast.error("No valid project selected");
      return;
    }
    
    try {
      const result = await runAnalysisMutation.mutateAsync({ projectId: selectedProjectId });
      
      // Show appropriate toast based on result
      if (result.addedCount === 0) {
        toast.info("No new tasks found.");
      } else {
        toast.success(`Added ${result.addedCount} new task${result.addedCount === 1 ? '' : 's'}.`);
      }
    } catch (error) {
      // Error toast already handled by mutation onError
    }
  }, [selectedProjectId, runAnalysisMutation]);

  const createProject = React.useCallback(async (name: string, baseUrl: string) => {
    await createProjectMutation.mutateAsync({ name, baseUrl });
  }, [createProjectMutation]);

  const createTask = React.useCallback(async (
    title: string,
    description: string | null,
    category: TaskCategory,
    impact: TaskImpact,
    effort: TaskEffort | null,
    relatedUrl: string | null,
    sourceLabel: string,
    recommendedActions: string[] | null,
  ) => {
    if (!selectedProjectId || !isValidUuid(selectedProjectId)) {
      toast.error("No valid project selected");
      return;
    }
    await createTaskMutation.mutateAsync({
      projectId: selectedProjectId,
      title,
      description,
      category,
      impact,
      effort,
      relatedUrl,
      sourceLabel,
      recommendedActions,
    });
  }, [selectedProjectId, createTaskMutation]);

  const updateTask = React.useCallback(async (
    id: string,
    title: string,
    description: string | null,
    category: TaskCategory,
    impact: TaskImpact,
    effort: TaskEffort | null,
    relatedUrl: string | null,
    sourceLabel: string,
    status: TaskStatus,
    recommendedActions: string[] | null,
  ) => {
    await updateTaskMutation.mutateAsync({
      id,
      title,
      description,
      category,
      impact,
      effort,
      relatedUrl,
      sourceLabel,
      status,
      recommendedActions,
    });
  }, [updateTaskMutation]);

  const deleteTask = React.useCallback(async (id: string) => {
    await deleteTaskMutation.mutateAsync({ id });
  }, [deleteTaskMutation]);

  const toggleTaskCompleted = React.useCallback(async (id: string) => {
    await toggleTaskCompletedMutation.mutateAsync({ id });
  }, [toggleTaskCompletedMutation]);

  const exportCsv = React.useCallback(async () => {
    if (!selectedProjectId || !isValidUuid(selectedProjectId)) {
      toast.error("No valid project selected");
      return;
    }
    const result = await exportCsvMutation.mutateAsync({
      projectId: selectedProjectId,
      filters: {
        query: filters.query || undefined,
        status: filters.status !== "all" ? filters.status : undefined,
        category: filters.category !== "all" ? filters.category : undefined,
        impact: filters.impact !== "all" ? filters.impact : undefined,
      },
    });

    // Trigger download
    const blob = new Blob([result.content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success("CSV exported successfully");
  }, [selectedProjectId, filters, exportCsvMutation]);

  const exportPdf = React.useCallback(async () => {
    if (!selectedProjectId || !isValidUuid(selectedProjectId)) {
      toast.error("No valid project selected");
      return;
    }
    const result = await exportPdfMutation.mutateAsync({
      projectId: selectedProjectId,
      filters: {
        query: filters.query || undefined,
        status: filters.status !== "all" ? filters.status : undefined,
        category: filters.category !== "all" ? filters.category : undefined,
        impact: filters.impact !== "all" ? filters.impact : undefined,
      },
    });

    // For PDF, we would typically convert HTML to PDF on client or use a service
    // For now, create an HTML file download as placeholder
    const blob = new Blob([result.content], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename.replace('.pdf', '.html');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success("PDF exported successfully");
  }, [selectedProjectId, filters, exportPdfMutation]);

  const loadTasks = React.useCallback(async () => {
    await utils.main.tasks.list.refetch();
  }, [utils]);

  return {
    // State
    projects,
    projectSearchQuery,
    selectedProjectId,
    selectedProject,
    kpis,
    categoryBreakdown,
    recentActivity,
    lastAnalysisFailed,
    lastAnalysisErrorMessage,
    tasks,
    filters,
    pagination,
    selectedTaskIds,
    isLoading,
    error,
    isRunningAnalysis,

    // Mutations
    setProjectSearchQuery,
    setTaskQuery,
    setTaskStatusFilter,
    setTaskCategoryFilter,
    setTaskImpactFilter,
    clearTaskFilters,
    setPage,
    setPageSize,
    setSelectedTaskIds: setSelectedTaskIdsMutation,
    toggleTaskSelected,
    clearSelection,

    // Commands
    initialize,
    selectProject,
    runAnalysis,
    createProject,
    createTask,
    updateTask,
    deleteTask,
    toggleTaskCompleted,
    exportCsv,
    exportPdf,
    loadTasks,
  };
}